import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';

import { Layouts } from 'app-components';
import { setSelectedMember } from '@actions/common/actionCreators';
import {
    fetchConsumerInboxUnreadNotificationCount
} from '@actions/individualPortal/secureInbox/secureInboxActionCreators';
import { fetchSideNavUnreadNotificationCount, setSideNavdetails } from '@actions/SideNav/sideNavActionCreators';
import { USER_PROFILE } from '@constants/common/statusTypes';
import NavBarContainer from '@containers/common/NavBarContainer';
import useQueryParams from '@hooks/useQueryParams';
import { IRemoteProps } from '@modules/members/enrollments/common/types';
import { getSideNavDetails, getUnreadMessageCountForSideNav } from '@reducers/appReducer';
import { environmentSelector } from '@selectors/commonSelectors';
import AdminMemberSideNav from './AdminMemberSideNav';
import { fetchPermissionDetails } from '@actions/memberPortal/enrollmentsActionCreators';
import { useAppSelector, useBroker } from '@hooks/index';

const LayoutContainer = styled.div`
  .grid-row {
    padding: 0;
  }

  .grid-col-12 {
    padding: 0;
  }

  #gi-sidebar {
    padding-left: 0;
    padding-right: 24px;
  }
`;

interface ISideNavProps {
  actions: {
    setSelectedMember: ( member: any ) => void;
  };
  activeModuleName: string;
  selectedMember: any;
  environment: any;
  children: React.ReactNode;
  page: string;
}

const SideNav = props => {
    const {environment, extProps, capTicketEnabled, numberUnread, page, updatePropsForAccUnlock} = props;
    return (
        <aside className="desktop:grid-col-3" id="gi-sidebar" data-testid="gi-sidebar">
            <AdminMemberSideNav
                environment={environment}
                extProps={extProps}
                capTicketEnabled={capTicketEnabled}
                unreadMessageCount={numberUnread}
                page={page}
                updateUnblockResponse={updatePropsForAccUnlock}
            />
        </aside>
    );
};

const SideNavContainer: React.FC<ISideNavProps> = ( {
    actions: {setSelectedMember},
    activeModuleName,
    selectedMember,
    environment,
    children,
    page,
} ) => {
    const dispatch = useDispatch();
    const queryParams = useQueryParams();
    const applicationSessionData = {} as IRemoteProps;
    const [ updateProps, setUpdateProps ] = useState(false);
    const { capTicketEnabled } = useSelector(environmentSelector);
    const {id} = useParams();
    const hhIdFromQueryParams = queryParams.get('hhid');
    const sideNavDetails = useSelector(getSideNavDetails);
    const numberUnread = useSelector(getUnreadMessageCountForSideNav);
    const enrollmentDetails = useAppSelector(state => state.memberEnrollment?.permissions);
    const { isHouseIconVisible } = useBroker();

    useEffect(() => {
        if (selectedMember && id && selectedMember?.encHouseHoldId !== id) {
            setSelectedMember({...selectedMember, encHouseHoldId: id});
        }
    }, [ selectedMember, id, setSelectedMember ]);

    useEffect(() => {
        const fetchData = async () => {
            const updateApplicationSessionData = () => {
                if (sessionStorage) {
                    Object.entries(sessionStorage).forEach(( item: [ string, any ] ) => {
                        applicationSessionData[item[0]] = item[1];
                    });
                }
                setSelectedMember(applicationSessionData);
            };

            const parsePermissionMap = ( permissionMap: any ) => {
                if (!permissionMap) return null;
                const t = JSON.stringify(permissionMap).replace(/[{}"]/g, '');
                const table = t.split(', ').map(pair => pair.split('='));
                return Object.fromEntries(table);
            };

            updateApplicationSessionData();
            const permissionMapRes = parsePermissionMap(applicationSessionData.permissionMap);
            dispatch(fetchSideNavUnreadNotificationCount('householdId', hhIdFromQueryParams || applicationSessionData.householdId));
            dispatch(
                setSideNavdetails({
                    idmUserStatus: applicationSessionData.idmUserStatus,
                    userStatus: applicationSessionData.userStatus,
                    hhUserId: applicationSessionData.householdUserId,
                    hhFirstName: applicationSessionData.householdFirstName,
                    hhLastName: applicationSessionData.householdLastName,
                    encHHId: applicationSessionData.encHouseHoldId,
                    hhId: applicationSessionData.householdId || '',
                    hhUser: applicationSessionData.householdUser,
                    calheersEnv: applicationSessionData.calheersEnv,
                    addNewViewCaseUrl: applicationSessionData.addNewViewCaseUrl,
                    checkDilog: applicationSessionData.checkDilog,
                    permissionMap: permissionMapRes,
                    switchToModuleName: applicationSessionData.switchToModuleName,
                    hhEmail: applicationSessionData.householdEmail,
                    hhPhone: applicationSessionData.householdPhone,
                    hhUserStatus: applicationSessionData.householdUserStatus,
                    activeModuleName,
                }),
            );
            dispatch(fetchConsumerInboxUnreadNotificationCount('householdId', id || ''));
        };
        localStorage.HHID = sideNavDetails.hhId;
        fetchData();
    }, [ updateProps, activeModuleName, setSelectedMember ]);

    useEffect(() => {
        const fetchSideNav = (encHHId) => {
            dispatch(fetchPermissionDetails(encHHId));
        };
        if (!applicationSessionData?.encHouseHoldId) fetchSideNav(id || hhIdFromQueryParams);
    }, [ hhIdFromQueryParams, id ]);

    useEffect(() => {
        const sessionHHId = sessionStorage.getItem('encHouseHoldId');
        if (!enrollmentDetails || sessionHHId || enrollmentDetails?.permissionMap) return;
        const permissionMap = Object.entries(enrollmentDetails.menuPermissions).reduce((acc, item) => {
            acc[item[0]] = item[1] ? 'true' : 'false';
            return acc;
        }, {});

        const lcSwitchModuleName = localStorage.getItem('lcSwitchModuleName');
        const hhEmail = localStorage.getItem('lcHhEmail');

        const sideNavDetails = {
            idmUserStatus: enrollmentDetails.idmUserStatus,
            userStatus: enrollmentDetails.userStatus,
            hhFirstName: enrollmentDetails.household.firstName,
            hhLastName: enrollmentDetails.household.lastName,
            encHHId: id || hhIdFromQueryParams,
            hhId: enrollmentDetails.householdId || '',
            hhUser: enrollmentDetails.household,
            permissionMap,
            hhPhone: enrollmentDetails.household.phoneNumber,
            hhEmail,
            switchToModuleName: lcSwitchModuleName,
        };

        dispatch(setSideNavdetails(sideNavDetails));

    }, [enrollmentDetails]);

    const updatePropsForAccUnlock = () => {
        sessionStorage.setItem('userStatus', USER_PROFILE.ACTIVE);
        sessionStorage.setItem('hhUserStatus', USER_PROFILE.ACTIVE);
        sessionStorage.setItem('idmUserStatus', USER_PROFILE.ENABLED);
        setUpdateProps(true);
    };

    const {hhFirstName, hhLastName, hhId} = sideNavDetails;

    return (
        <LayoutContainer>
            <Layouts id="sidenav-layout">
                <Layouts.Row hasGap>
                    <div className="grid-col-12">
                        <div className="grid-row">
                            <NavBarContainer
                                navBarClass="ticket-mgmt-top-nav grid-col-12"
                                houseIcon={isHouseIconVisible}
                            />
                        </div>
                        <div className="grid-row padding-t-20">
                            <h1 className="householdInfo" data-testid="householdInfo">
                                {hhFirstName} {hhLastName} (ID: {hhId})
                            </h1>
                        </div>
                    </div>
                    <SideNav
                        {...{
                            environment,
                            extProps: sideNavDetails,
                            capTicketEnabled,
                            numberUnread,
                            page,
                            updatePropsForAccUnlock,
                        }}
                    />
                    {children}
                </Layouts.Row>
            </Layouts>
        </LayoutContainer>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    activeModuleName: state.common.environment.user.activeModuleName,
    selectedMember: state.common?.sessionDataReducer?.selectedMember,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({setSelectedMember}, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(SideNavContainer);
